import { Bot, Check, Eye, FileSearch, KeyRound, LockKeyhole, Radio, ScanSearch, ShieldCheck, Sparkles, Workflow } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, api } from "../api";
import { Button, CopyButton, Logo, ProviderMark } from "../components";
import { LiveReceiver, type ReceiverState } from "./LiveReceiver";
import { claimTokenFromUrl } from "./claimPreview";
import type { ProviderId } from "../../shared/providers";
import "./onboarding.css";

type Provider = ProviderId;
type BootstrapResult = { prompt: string; claimUrl: string; expiresAt: string; provider: Provider };

const providers: Array<{ id: Provider; name: string; detail: string }> = [
  { id: "codex", name: "Codex", detail: "Tasks, automations, repos" },
  { id: "claude", name: "Claude", detail: "Projects, skills, schedules" },
  { id: "grok", name: "Grok", detail: "Grok Bot, workspaces, routines" },
  { id: "muse", name: "Muse", detail: "Standing tasks and connectors" },
  { id: "dots", name: "dots", detail: "Goals, plugins, schedules" },
  { id: "other", name: "Another agent", detail: "Any MCP or HTTPS agent" },
];

function restoreBootstrap(): BootstrapResult | null {
  try {
    const value = sessionStorage.getItem("lg.pendingClaim");
    return value ? (JSON.parse(value) as BootstrapResult) : null;
  } catch {
    return null;
  }
}

export function StartPage() {
  const [generated, setGenerated] = useState<BootstrapResult | null>(restoreBootstrap);
  const [provider, setProvider] = useState<Provider>(() => generated?.provider ?? "codex");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [receiverState, setReceiverState] = useState<ReceiverState>("waiting");

  async function createPrompt() {
    setBusy(true);
    setError("");
    try {
      const result = await api<BootstrapResult>("/api/bootstrap", { method: "POST", body: JSON.stringify({ provider }) });
      sessionStorage.setItem("lg.pendingClaim", JSON.stringify(result));
      setGenerated(result);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "The secure prompt could not be created");
    } finally {
      setBusy(false);
    }
  }

  function choose(next: Provider) {
    setProvider(next);
    if (generated && generated.provider !== next) {
      sessionStorage.removeItem("lg.pendingClaim");
      setGenerated(null);
    }
  }

  const selected = providers.find((item) => item.id === provider) ?? providers[0];
  const token = generated ? claimTokenFromUrl(generated.claimUrl) : "";
  const arrived = Boolean(generated) && (receiverState === "ready" || receiverState === "claimed");
  const claimed = Boolean(generated) && receiverState === "claimed";
  const stepClass = (done: boolean, active: boolean) => (done ? "is-done" : active ? "is-active" : "");

  return (
    <div className="agent-start journey-public journey-start">
      <nav className="agent-start__nav journey-nav"><Link to="/" aria-label="LoopGroup home"><Logo /></Link><span>Private entry point <i aria-hidden="true" /> Already claimed yours? <Link to="/signin">Sign in</Link></span></nav>
      <main className="agent-start__main">
        <header className="agent-start__hero">
          <p className="eyebrow"><Sparkles size={11} aria-hidden="true" /> Private scan · no account needed</p>
          <h1>Send this prompt<br />to your <em>agent.</em></h1>
          <p>Your agent maps the recurring work it can substantiate into a temporary private catalog. <strong>Scan only. Nothing is edited or scheduled.</strong></p>
        </header>

        <ol className="agent-start__steps" aria-label="Three steps">
          <li className={stepClass(Boolean(generated), !generated)}><i>{generated ? <Check size={12} aria-hidden="true" /> : "01"}</i><b>Create the prompt</b><small>{generated ? "Created with a revocable report key" : "No account or loop edit required"}</small></li>
          <li className={stepClass(arrived, Boolean(generated) && !arrived)}><i>{arrived ? <Check size={12} aria-hidden="true" /> : "02"}</i><b>Send it to {selected.name}</b><small>{arrived ? "Loops arrived in the receiver" : generated ? "Copy it, paste it, and watch the receiver" : "The receiver listens while you do"}</small></li>
          <li className={stepClass(claimed, arrived && !claimed)}><i>{claimed ? <Check size={12} aria-hidden="true" /> : "03"}</i><b>Claim your loops</b><small>{claimed ? "Claimed; observers and reporting stay off until you choose" : "Observers and reporting stay off until you choose"}</small></li>
        </ol>

        <section className="agent-start__workspace" aria-label="Prompt and receiver">
          <div className="agent-start__compose">
            <p className="eyebrow">Where do these loops live?</p>
            <div className="agent-start__providers">
              {providers.map((item) => (
                <button key={item.id} type="button" className={provider === item.id ? "is-selected" : ""} onClick={() => choose(item.id)} aria-pressed={provider === item.id}>
                  <ProviderMark provider={item.id} size={32} /><b>{item.name}</b><small>{item.detail}</small>{provider === item.id && <Check size={13} aria-hidden="true" />}
                </button>
              ))}
            </div>

            <div className={`agent-start__prompt ${generated ? "is-ready" : ""}`}>
              <div className="agent-start__prompt-head"><span><Bot size={15} aria-hidden="true" /> {generated ? "Prompt ready" : "Private receiver"} · {selected.name}</span><span><LockKeyhole size={12} aria-hidden="true" /> revocable report key</span></div>
              {generated ? (
                <>
                  <pre>{generated.prompt}</pre>
                  <div className="agent-start__prompt-actions">
                    <CopyButton variant={arrived ? "secondary" : "primary"} value={generated.prompt} label={`Copy prompt for ${selected.name}`} />
                    <span className="agent-start__prompt-hint">{arrived ? `${selected.name} has synced. Claim the loops in the receiver, or send the prompt again to refresh the catalog.` : `Paste it into ${selected.name}. The scan is read-only; the receiver fills in as loops arrive.`}</span>
                  </div>
                </>
              ) : (
                <div className="agent-start__prompt-empty">
                  <ScanSearch size={28} aria-hidden="true" />
                  <h2>Create a private receiver for {selected.name}.</h2>
                  <p>The prompt carries a revocable report key for one metadata sync. Your separate claim link stays in this browser and is never given to the agent.</p>
                  <Button onClick={() => void createPrompt()} busy={busy}><KeyRound size={15} aria-hidden="true" /> Create my scan prompt</Button>
                </div>
              )}
            </div>
            {error && <div className="form-error journey-inline-error" role="alert"><strong>Prompt creation failed.</strong><span>{error}</span><small>No receiver or key was created. Check the message, then try again.</small></div>}
          </div>

          {generated && token ? (
            <LiveReceiver token={token} claimUrl={generated.claimUrl} onStateChange={setReceiverState} />
          ) : (
            <aside className="agent-start__explain">
              <div className="agent-start__signal" aria-hidden="true"><i /><i /><i /><span><Radio size={20} /></span></div>
              <p className="eyebrow">Private scan boundary</p>
              <h2>A private map. Nothing installed.</h2>
              <div className="agent-start__explain-list">
                <span><FileSearch size={16} aria-hidden="true" /><div><b>Reads accessible configuration</b><small>Schedules, task lists, workspace instructions, and repeatable work the agent can substantiate.</small></div></span>
                <span><Eye size={16} aria-hidden="true" /><div><b>Sends metadata and evidence</b><small>Name, purpose, cadence, source, confidence, and blind spots. Never private content.</small></div></span>
                <span><ShieldCheck size={16} aria-hidden="true" /><div><b>Changes nothing</b><small>No loop edits, no observer, no recurring schedule, and no invented run outcomes.</small></div></span>
              </div>
              <footer><LockKeyhole size={12} aria-hidden="true" /><span><b>Stays local</b> Secrets, prompts, conversations, customer data, documents, and outputs.</span></footer>
            </aside>
          )}
        </section>

        <section className="agent-start__after">
          <div><p className="eyebrow">After you claim</p><h2>You decide how the map becomes live.</h2><p>Cataloging and performance reporting stay separate so LoopGroup never pretends a discovered schedule is a successful run.</p></div>
          <article><Eye size={18} aria-hidden="true" /><span><b>Catalog observer</b><small>Keeps names, cadence, and newly discovered loops current. It does not decide whether a run succeeded.</small></span><i>Optional</i></article>
          <article><Workflow size={18} aria-hidden="true" /><span><b>Per-loop run reporting</b><small>Adds non-blocking start and finish signals only to loops you explicitly approve after reviewing the edits.</small></span><i>Per loop</i></article>
        </section>
      </main>
    </div>
  );
}
